// src/shared/components/TopNavigation.tsx
import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface BreadcrumbItem {
  path: string;
  title: string;
}

// Route to title mapping for breadcrumbs
const routeTitleMap: Record<string, string> = {
  '/dashboard': 'داشبورد',
  '/remittance': 'حواله‌ها',
  '/waybills': 'بارنامه‌ها',
  '/drivers': 'رانندگان',
  '/companies': 'شرکت‌ها',
  '/company-users': 'کاربران شرکت',
  '/user-activity': 'فعالیت کاربران',
  '/cities': 'اطلاعات پایه',
  '/cars': 'خودروها',
  '/branches': 'شعبه‌ها',
  '/offices': 'دفترها',
  '/goods': 'کالاها',
  '/product-owners': 'صاحبان کالا',
  '/waybill-numbers': 'شماره‌های بارنامه',
  '/remittance-numbers': 'شماره‌های حواله',
  '/sender-receiver-search': 'جستجوی فرستنده/گیرنده',
  '/financial-coefficients': 'ضرایب مالی',
  '/insurance-contracts': 'قراردادهای بیمه',
  '/reports': 'گزارشات',
  '/reports/all': 'همه گزارش‌ها',
  '/reports/financial': 'گزارش مالی بارنامه',
  '/reports/expense': 'گزارش هزینه',
  '/reports/insurance': 'گزارش بیمه',
  '/reports/tonnage': 'گزارش تناژ',
  '/reports/tax-duties': 'گزارش مالیات و عوارض',
  '/reports/summary-coefficients': 'خلاصه ضرایب',
  '/reports/cargo-summary': 'خلاصه بار',
  '/reports/sender-summary': 'خلاصه فرستنده',
  '/reports/receiver-summary': 'خلاصه گیرنده',
  '/tickets': 'تیکت‌ها',
  '/admin/tickets': 'مدیریت تیکت‌ها',
  '/customers': 'مشتریان',
  '/withdraw-requests': 'درخواست‌های برداشت',
  '/wallet': 'کیف پول',
  '/change-password': 'تغییر رمز عبور من',
  '/bank-accounts': 'حساب‌های بانکی',
  '/certificates': 'گواهینامه‌ها',
  '/facilities': 'امکانات',
  '/financial-guide': 'راهنمای واحد مالی',
  '/fare-inquiry': 'استعلام کرایه',
  '/remittance/recover-by-introduction': 'جست و جوی حواله با کد رهگیری',
  '/suspicious-waybills': 'بارنامه‌های مشکوک',
  // Create routes
  '/waybills/new': 'ایجاد بارنامه',
  '/remittance/new': 'ایجاد حواله',
  '/drivers/new': 'ایجاد راننده',
  '/companies/new': 'ایجاد شرکت',
  '/company-users/new': 'ایجاد کاربر شرکت',
  '/customers/new': 'ایجاد مشتری',
  '/withdraw-requests/new': 'ایجاد درخواست برداشت',
  '/bank-accounts/new': 'ایجاد حساب بانکی',
  '/certificates/new': 'ایجاد گواهینامه',
  '/tickets/new': 'ایجاد تیکت',
  // View/Show routes
  '/waybill-show': 'مشاهده بارنامه',
  '/remittance/edit': 'ویرایش حواله',
  '/driver-show': 'مشاهده راننده',
  '/company-show': 'مشاهده شرکت',
  '/customer-show': 'مشاهده مشتری',
  '/ticket-show': 'مشاهده تیکت',
  '/withdraw-request-show': 'مشاهده درخواست برداشت',
  '/bank-account-show': 'مشاهده حساب بانکی',
  '/certificate-show': 'مشاهده گواهینامه',
  // Edit routes
  '/waybillEdit': 'ویرایش بارنامه',
  '/remittanceEdit': 'ویرایش حواله',
  '/driverEdit': 'ویرایش راننده',
  '/companyEdit': 'ویرایش شرکت',
  '/customerEdit': 'ویرایش مشتری',
  '/ticketEdit': 'ویرایش تیکت',
  '/withdraw-requestEdit': 'ویرایش درخواست برداشت',
  '/bank-accountEdit': 'ویرایش حساب بانکی',
  '/certificateEdit': 'ویرایش گواهینامه',
  // History routes
  '/waybills/history': 'سوابق بارنامه',
  '/remittance/history': 'سوابق حواله',
  // Payment routes
  '/waybills/payments': 'پرداخت‌های بارنامه',
  '/remittance/payments': 'پرداخت‌های حواله',
  // Print routes
  '/waybills/print': 'چاپ بارنامه',
  '/remittance/print': 'چاپ حواله',
  // Tracking routes
  '/remittance/tracking': 'پیگیری حواله',
  '/waybills/tracking': 'پیگیری بارنامه',
  // Discharge routes
  '/remittance/discharge': 'تخلیه حواله',
  '/waybills/discharge': 'تخلیه بارنامه',
  '/base-information': 'اطلاعات پایه',
  '/base-information/product-owners': 'مالکین کالا',
  '/transceivers': 'لیست فرستنده گیرنده',
  '/settings': 'تنظیمات',
};

const TopNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Build breadcrumbs from current path
  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    
    // If on dashboard, return empty array (no breadcrumbs needed)
    if (pathname === '/' || pathname === '/dashboard') {
      return [];
    }

    if (pathname === '/financial-guide') {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/dashboard?tab=5&workspace=overview', title: 'واحد مالی' },
        { path: '/financial-guide', title: 'راهنمای واحد مالی' },
      ];
    }
    
    // Print pages: show حواله‌ها > چاپ حواله or بارنامه‌ها > چاپ بارنامه (no id in breadcrumb)
    if (/^\/remittance\/print(\/|$)/.test(pathname)) {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/remittance', title: 'حواله‌ها' },
        { path: '/remittance/print', title: 'چاپ حواله' },
      ];
    }
    if (/^\/waybills\/print(\/|$)/.test(pathname)) {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/waybills', title: 'بارنامه‌ها' },
        { path: '/waybills/print', title: 'چاپ بارنامه' },
      ];
    }

    // Special case for transceivers page
    if (pathname === '/transceivers') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/transceivers', title: 'لیست فرستنده گیرنده' },
      ];
    }
    
    // Special case for cars page
    if (pathname === '/base-information/cars') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/base-information/cars', title: 'وسایل نقلیه' },
      ];
    }

    // Special case for goods (products) list page
    if (pathname === '/base-information/goods') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/base-information/goods', title: 'فهرست کالا‌ها' },
      ];
    }

    // Special case for cities list page
    if (pathname === '/base-information/cities') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/base-information/cities', title: 'فهرست شهرها' },
      ];
    }

    // Special case for product owners list
    if (pathname === '/base-information/product-owners') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/base-information/product-owners', title: 'مالکین کالا' },
      ];
    }
    
    // Special case for drivers page
    if (pathname === '/drivers') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/drivers', title: 'رانندگان' },
      ];
    }
    
    // Special case for financial-coefficients page
    if (pathname === '/financial-coefficients') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/financial-coefficients', title: 'ضرایب مالی' },
      ];
    }
    
    // Special case for waybill-numbers page
    if (pathname === '/waybill-numbers') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/waybill-numbers', title: 'شماره‌های بارنامه' },
      ];
    }
    
    // Special case for insurance-contracts page
    if (pathname === '/insurance-contracts') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/insurance-contracts', title: 'قراردادهای بیمه' },
      ];
    }
    
    // Special case for bank-accounts page
    if (pathname === '/bank-accounts') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/bank-accounts', title: 'حساب‌های بانکی' },
      ];
    }
    
    // Special case for customers page
    if (pathname === '/customers') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/customers', title: 'مشتریان' },
      ];
    }
    
    // Special case for branches page
    if (pathname === '/branches') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/branches', title: 'شعبه‌ها' },
      ];
    }

    
    // Special case for company users page
    if (pathname === '/company/users') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/company/users', title: 'کاربران من' },
      ];
    }

    if (pathname === '/settings/user-print-stamps') {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/dashboard?tab=4', title: 'امکانات' },
        { path: '/settings/user-print-stamps', title: 'مهرهای بارگذاری‌شده توسط کاربران' },
      ];
    }

    if (pathname === '/settings/print-stamps') {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/dashboard?tab=4', title: 'امکانات' },
        { path: '/settings/print-stamps', title: 'مهرهای چاپ بارنامه' },
      ];
    }

    // Special case for settings page: داشبورد - امکانات - تنظیمات
    if (pathname === '/settings') {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/settings', title: 'امکانات' },
        { path: '/settings', title: 'تنظیمات' },
      ];
    }
    
    // Special case for گزارش مالی بارنامه: داشبورد > گزارشات (?tab=3) > گزارش مالی بارنامه
    if (pathname === '/reports/financial') {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/dashboard?tab=3', title: 'گزارشات' },
        { path: '/reports/financial', title: 'گزارش مالی بارنامه' },
      ];
    }

    // Special case for waybill edit pages: داشبورد > بارنامه ها > ویرایش بارنامه (no ID in breadcrumb)
    if (pathname.startsWith('/waybillEdit/') || pathname.startsWith('/waybill/edit/') || pathname.startsWith('/waybills/edit/')) {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/waybills', title: 'بارنامه ها' },
        { path: pathname, title: 'ویرایش بارنامه' },
      ];
    }
    
    // Special case for waybill create pages: داشبورد > بارنامه ها > ایجاد بارنامه
    if (pathname.startsWith('/waybills/new')) {
      return [
        { path: '/dashboard', title: 'داشبورد' },
        { path: '/waybills', title: 'بارنامه ها' },
        { path: pathname, title: 'ایجاد بارنامه' },
      ];
    }
    
    // Special case for financial-coefficients create page
    if (pathname === '/financial-coefficients/create') {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/financial-coefficients', title: 'ضرایب مالی' },
        { path: '/financial-coefficients/create', title: 'ایجاد ضریب مالی' },
      ];
    }

    // Special case for financial-coefficients edit page
    if (pathname.startsWith('/financial-coefficients/edit/')) {
      return [
        { path: '/dashboard?tab=1', title: 'داشبورد' },
        { path: '/dashboard?tab=2', title: 'اطلاعات پایه' },
        { path: '/financial-coefficients', title: 'ضرایب مالی' },
        { path: pathname, title: 'ویرایش ضریب مالی' },
      ];
    }
    
    const crumbs: BreadcrumbItem[] = [];
    const pathSegments = pathname.split('/').filter(Boolean);
    
    // Only the admin route namespace belongs to the admin dashboard.
    const isAdminPath = pathname === '/admin' || pathname.startsWith('/admin/');
    const dashboardPath = isAdminPath ? '/admin/dashboard' : '/dashboard';
    
    // Always start with dashboard
    crumbs.push({ path: dashboardPath, title: 'داشبورد' });

    // Build path progressively
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const fullPath = currentPath;
      
      // Get title from map or use segment
      let title = routeTitleMap[fullPath];
      
      // If not found, try to infer from segment
      if (!title) {
        // Check if it's an ID (numeric or UUID-like)
        if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
          // It's an ID - check if parent path is an edit or create route
          const parentPath = pathSegments.slice(0, index).join('/');
          const parentFullPath = `/${parentPath}`;
          const parentTitle = routeTitleMap[parentFullPath];
          
          // If parent is an edit route (e.g. /remittance/edit, /waybills/edit), skip adding ID breadcrumb
          if (parentFullPath.includes('/edit') || (parentTitle && parentTitle.includes('ویرایش'))) {
            return; // Skip this segment - don't add breadcrumb for ID after edit route
          }
          // If parent is waybill create (/waybills/new), skip adding ID breadcrumb – show only "ایجاد بارنامه"
          if (parentFullPath === '/waybills/new') {
            return; // Skip – breadcrumb stays: داشبورد > بارنامه‌ها > ایجاد بارنامه
          }
          
          // Otherwise, get title from parent path
          const singularTitle = (parentTitle || routeTitleMap[`/${pathSegments.slice(0, index - 1).join('/')}`] || 'آیتم').replace(/‌ها$/, '').replace(/ها$/, '');
          title = `جزئیات ${singularTitle}`;
        } else {
          // Try common patterns
          if (segment === 'new' || segment === 'create') {
            const parentPath = pathSegments.slice(0, index).join('/');
            const parentTitle = routeTitleMap[`/${parentPath}`] || 'آیتم';
            const singularTitle = parentTitle.replace(/‌ها$/, '').replace(/ها$/, '');
            title = `ایجاد ${singularTitle}`;
          } else if (segment === 'edit') {
            const parentPath = pathSegments.slice(0, index).join('/');
            const parentTitle = routeTitleMap[`/${parentPath}`] || 'آیتم';
            const singularTitle = parentTitle.replace(/‌ها$/, '').replace(/ها$/, '');
            title = `ویرایش ${singularTitle}`;
          } else if (segment === 'show' || segment === 'view') {
            const parentPath = pathSegments.slice(0, index).join('/');
            const parentTitle = routeTitleMap[`/${parentPath}`] || 'آیتم';
            const singularTitle = parentTitle.replace(/‌ها$/, '').replace(/ها$/, '');
            title = `مشاهده ${singularTitle}`;
          } else if (segment === 'tracking') {
            const parentPath = pathSegments.slice(0, index).join('/');
            const parentTitle = routeTitleMap[`/${parentPath}`] || 'آیتم';
            const singularTitle = parentTitle.replace(/‌ها$/, '').replace(/ها$/, '');
            title = `پیگیری ${singularTitle}`;
          } else if (segment === 'discharge') {
            const parentPath = pathSegments.slice(0, index).join('/');
            const parentTitle = routeTitleMap[`/${parentPath}`] || 'آیتم';
            const singularTitle = parentTitle.replace(/‌ها$/, '').replace(/ها$/, '');
            title = `تخلیه ${singularTitle}`;
          } else if (segment === 'payments' || segment === 'payment') {
            const parentPath = pathSegments.slice(0, index).join('/');
            const parentTitle = routeTitleMap[`/${parentPath}`] || 'آیتم';
            const singularTitle = parentTitle.replace(/‌ها$/, '').replace(/ها$/, '');
            title = `پرداخت‌های ${singularTitle}`;
          } else if (segment === 'history') {
            const parentPath = pathSegments.slice(0, index).join('/');
            const parentTitle = routeTitleMap[`/${parentPath}`] || 'آیتم';
            const singularTitle = parentTitle.replace(/‌ها$/, '').replace(/ها$/, '');
            title = `سوابق ${singularTitle}`;
          } else {
            // Use segment as title (capitalize first letter)
            title = segment;
          }
        }
      }

      // Don't add duplicate dashboard
      if (fullPath !== '/') {
        crumbs.push({ path: fullPath, title });
      }
    });

    return crumbs;
  }, [location.pathname]);

  return (
    <nav className="app-navigation bg-white shadow-sm border-b border-gray-100 sticky top-[var(--app-header-height,74px)] z-20 min-w-0" dir="rtl">
      {/* Breadcrumbs - Show only when not on dashboard and there are breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <div className="app-breadcrumbs bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-50 px-2 sm:px-4 2xl:px-6">
          <div className="flex min-h-[var(--app-breadcrumb-height,64px)] items-center gap-1.5 overflow-x-auto py-2 text-xs sm:gap-2 sm:text-sm">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const isFirst = index === 0;
              const showSeparator = !isFirst;
              
              return (
                <React.Fragment key={`${crumb.path}-${index}`}>
                  {showSeparator && (
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0 mx-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 5l-7 7 7 7"
                      />
                    </svg>
                  )}
                  {isLast ? (
                    <span className="shrink-0 whitespace-nowrap text-gray-800 font-semibold flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg shadow-sm border border-gray-200/50">
                      <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 14.707a1 1 0 010-1.414L9.414 10l3.293-3.293a1 1 0 00-1.414-1.414l-4 4a1 1 0 000 1.414l4 4a1 1 0 001.414 0z" clipRule="evenodd" />
                      </svg>
                      {crumb.title}
                    </span>
                  ) : (
                    <Link
                      to={crumb.path}
                      className="shrink-0 whitespace-nowrap text-indigo-600 hover:text-indigo-700 font-medium transition-all duration-200 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/70 hover:shadow-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(crumb.path);
                      }}
                    >
                      {isFirst && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      )}
                      {crumb.title}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default TopNavigation;
